import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { Section } from '../../Section';
import { useSearchPropertiesLazyQuery } from '../../../types/graphqlHelpers';
import { CardCarouselWrapper } from '../../CardCarouselWrapper';
import { PropertyCard } from '../../properties/PropertyCard';
import { Loader } from '../../Loader';
import { UseBuildInfo } from '../../../hooks/useBuildInfo';
import { Button } from '../../Button';
import { Spacer } from '../../Spacer';

const StyledLoader = styled.div`
  height: 700px;
  display: grid;
  place-items: center;
`;

// Utility function to generate the search URL
const generateSearchUrl = (baseUrl, params) => {
  const queryString = Object.entries(params)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        // Join array values with commas without encoding
        return `${key}=${value.join(',')}`;
      }
      if (value !== null && value !== undefined) {
        // Encode non-array values
        return `${key}=${encodeURIComponent(value)}`;
      }
      return null; // Skip null/undefined values
    })
    .filter(Boolean) // Remove nulls
    .join('&'); // Join key-value pairs with '&'

  return `${baseUrl}?${queryString}`;
};

export const PropertyCarousel = ({ data: searchData, ...rest }) => {
  const [properties, setProperties] = useState([]);
  const buildInfo = UseBuildInfo();

  const [getProperties, { loading: searchLoading, error, data: propertyData, called }] = useSearchPropertiesLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const variables = {
    currencyid: 1,
    first: 12,
    page: 1,
    sort: searchData.sort,
    nearme: false,
    radius: 5,
    underoffer: false,
    newhomes: true,
    onlynewhomes: false,
    category: searchData.category,
    type: searchData.type,
    bedrooms: searchData.fBedroom,
    propertyStyle: searchData.fStyle,
    bathrooms: [],
    propertyType: searchData.fType,
    lastBuildDateTime: buildInfo?.lastBuild,
    multiSearch: searchData.multiSearch,
  };

  useEffect(() => {
    if (!called) {
      getProperties({ variables });
    }
  }, [searchData]);

  useEffect(() => {
    const searchResponse = propertyData?.properties?.propertySearch?.properties?.edges;

    if (searchResponse) {
      const searchResponseProperties = searchResponse.map((item) => item.node);
      setProperties(searchResponseProperties);
    }
  }, [propertyData]);

  // Generate the "View All" URL based on variables
  const viewAllUrl = generateSearchUrl('/search-results/', {
    category: variables.category,
    currencyid: variables.currencyid,
    first: variables.first,
    nearme: variables.nearme,
    newhomes: variables.newhomes,
    onlynewhomes: variables.onlynewhomes,
    page: variables.page,
    priceHighest: 100000000,
    priceLowest: 0,
    riverside: false,
    sort: variables.sort,
    type: variables.type,
    unavailable: false,
    underoffer: variables.underoffer,
    bedrooms: variables.bedrooms,
    propertyStyle: variables.propertyStyle,
    propertyType: variables.propertyType,
    multiSearch: variables.multiSearch,
  });

  if (searchLoading) {
    return (
      <Section contained dontPrint overflowHidden>
        <StyledLoader>
          <Loader centered />
        </StyledLoader>
      </Section>
    );
  }

  if (properties.length !== 0) {
    return (
      <Section contained dontPrint overflowHidden>
        <Spacer spacing="double">
          <CardCarouselWrapper heading={searchData.carouselDescription}>
            {properties.map((property) => (
              <PropertyCard
                property={property}
                vertical
                onWhiteBg
                key={`rv-${property.id}`}
                unSaveable
                prioritiseViewing
                displayMobileCta
              />
            ))}
          </CardCarouselWrapper>
          <Button url={viewAllUrl} variant="solid" label="See all properties" arrowRight />
        </Spacer>
      </Section>
    );
  }
};

export const PropertyCarouselQuery = graphql`
  fragment PropertyCarousel on KFH_UmbracoPropertyCarouselComponentType {
    status
    carouselDescription
    fType
    fStyle
    fBedroom
    displayName
    category
    multiSearch
    sort
    type
  }
`;
